import classNames from 'classnames'
import { FC } from 'react'
import {
  Band,
  DashboardScore,
} from 'src/services/aiListeningApi/brandProfile.types'
import styles from './dashboard.module.scss'
import { AreaChart } from '../charts/AreaChart'
import { MeterBar } from '../charts/MeterBar'
import { ScoreGauge } from '../charts/ScoreGauge'
import { BAND_TONES, Pill } from '../Pill'
import { BAND_LEGEND, bandOf, formatNumber } from '../utils'

const BAND_HEADLINES: Record<Band, string> = {
  attention: 'Needs attention',
  moderate: 'Moderate',
  strong: 'Strong',
}

// "**Strong and growing** +4 vs last period": the status is bold, the change
// after it regular.
const StatusLine: FC<{ band: Band; delta: number | null }> = ({
  band,
  delta,
}) => {
  const headline = BAND_HEADLINES[band]
  if (delta === null || !Number.isFinite(delta)) {
    return <strong className={styles.scoreStatus}>{headline}</strong>
  }
  const rounded = Math.round(delta)
  const trend = rounded > 0 ? 'growing' : rounded < 0 ? 'declining' : 'steady'
  return (
    <>
      <strong className={styles.scoreStatus}>
        {headline} and {trend}
      </strong>{' '}
      {rounded > 0 ? `+${rounded} ` : rounded < 0 ? `${rounded} ` : ''}vs last
      period
    </>
  )
}

interface ScoreCardProps {
  score: DashboardScore
}

export const ScoreCard: FC<ScoreCardProps> = ({ score }) => {
  const band = score.band ?? bandOf(score.value)

  return (
    <section className={classNames(styles.card, styles.scoreCard)}>
      <div className={styles.scoreGauge}>
        <ScoreGauge value={score.value} />
        {band && (
          <Pill dot size="md" tone={BAND_TONES[band]}>
            <StatusLine band={band} delta={score.delta} />
          </Pill>
        )}
      </div>

      <div className={styles.scoreDetails}>
        <h2 className={styles.scoreTitle}>AI Visibility Score</h2>
        {score.narrative && (
          // No full stop after the last sentence (QA, 2026-10-21).
          <p className={styles.scoreNarrative}>
            {score.narrative.replace(/\.\s*$/, '')}
          </p>
        )}
        <AreaChart
          className={styles.scoreTrend}
          height={56}
          points={score.series}
        />

        <div className={styles.subScores}>
          {score.subScores.map((subScore) => (
            <div className={styles.subScore} key={subScore.key}>
              <span className={styles.subScoreLabel}>{subScore.label}</span>
              <MeterBar
                band={subScore.band ?? bandOf(subScore.value)}
                value={subScore.value}
              />
              <span className={styles.subScoreValue}>
                {formatNumber(subScore.value)}
              </span>
            </div>
          ))}
        </div>

        <div className={styles.legend}>
          {BAND_LEGEND.map((item) => (
            <span className={styles.legendItem} key={item.band}>
              <span
                className={classNames(styles.legendDot, styles[item.band])}
              />
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}
